import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import clsx from 'clsx'
import zIndex from 'styles/tools/z-index'
import ExperienceSearchBar from './ExperienceSearchBar/ExperienceSearchBar'
import TextButton from 'LuxKit/Button/TextButton'
import Pane from 'components/Common/Pane'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import ExperienceSearchCategoryFilters from './ExperienceSearchCategoryFilters'
import MapSearchNav from 'components/NavKit/MapSearchNav'
import noop from 'lib/function/noop'
import LineArrowLeftIcon from 'LuxKit/Icons/line/LineArrowLeftIcon'
import ExperienceExposedFiltersHeader from './ExperienceExposedFiltersHeader'
import ExperiencesSearchControls from 'components/Search/Experiences/ExperiencesSearchControls'

const MobileHeader = styled(Pane)`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  padding: ${rem(12)} ${rem(16)};
  border-top: 1px solid ${props => props.theme.palette.neutral.default.six};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};
  position: sticky;
  top: 0;
  z-index: ${zIndex.searchSubMenu};

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const DesktopWrapper = styled(Pane)`
  display: none;

  ${mediaQueryUp.desktop} {
    display: grid;
    padding-top: ${rem(12)};
    padding-bottom: ${rem(12)};
    gap: ${rem(12)};
  }
`

const SearchFormContainer = styled(LayoutContainer)`
  display: block;

  &.map-view {
    flex: 1;
    padding: 0 ${rem(24)};
    display: flex;
    gap: ${rem(8)};
    width: 100%;
    max-width: 100%;
  }

  .search-controls {
    flex: 1;
  }
`

const StyledButton = styled(TextButton)`
  flex: 1;

  ${mediaQueryUp.tablet} {
    flex: unset
  }
`

const TabletExposedFiltersContainer = styled(LayoutContainer)`
  padding-top: 0;
  padding-bottom: ${rem(12)};
  border-bottom: 1px solid ${props => props.theme.palette.neutral.default.six};
`

const StickyChipsWrapper = styled(CSSBreakpoint)`
  position: sticky;
  top: 0;
  z-index: ${zIndex.searchSubMenu};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

interface Props {
  list: App.ApiCallState<{
    ids: Array<string>
    placeId: string
  }>
  toggleShowFilter?: () => void
  searchMode: Experiences.SearchMode
  place?: App.Place
}

function ExperienceSearchHeader(props: Props) {
  const { toggleShowFilter = noop, searchMode, place } = props
  const trackEvent = useTrackExperienceOfferEvent()
  const intl = useIntl()

  const handleViewAllClick = useCallback(() => {
    trackEvent('VIEW_ALL_CLICKED')
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- trackEvent is stable
  }, [])

  const isMapView = searchMode.mode === 'map'

  return <>
    <MobileHeader>
      <ExperienceSearchBar
        isMapView={isMapView}
        searchModeUrl={searchMode.url}
      />
      {!isMapView && (
        <ExperienceExposedFiltersHeader
          toggleFilterPanel={toggleShowFilter}
          place={place}
        />
      )}
    </MobileHeader>
    <DesktopWrapper>
      <SearchFormContainer className={clsx({ 'map-view': isMapView })}>
        {isMapView && <CSSBreakpoint min="tablet">
          <StyledButton
            kind="tertiary"
            size="large"
            onClick={handleViewAllClick}
            startIcon={<LineArrowLeftIcon />}
            fit="mobile-full-width"
            to={searchMode.url}
          >
            {intl.formatMessage({
              id: '_4K2AVKzyOGSbv_5CdYa',
              defaultMessage: 'List view',
              description: 'Button text to switch from map view to list view on experience search',
            })}
          </StyledButton>
        </CSSBreakpoint>}
        <ExperiencesSearchControls className="search-controls" isMapView={isMapView} />
        {isMapView && <MapSearchNav />}
      </SearchFormContainer>
      {!isMapView && <CSSBreakpoint only="tablet">
        <TabletExposedFiltersContainer size="xl">
          <ExperienceExposedFiltersHeader
            toggleFilterPanel={toggleShowFilter}
            place={place}
          />
        </TabletExposedFiltersContainer>
      </CSSBreakpoint>}
      {isMapView && <ExperienceSearchCategoryFilters place={place} isMapView />}
    </DesktopWrapper>
    {!isMapView && (
      <StickyChipsWrapper min="desktop">
        <ExperienceSearchCategoryFilters place={place} />
      </StickyChipsWrapper>
    )}
  </>
}

export default ExperienceSearchHeader
